/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Input,
  Checkbox,
  Spin,
  Typography,
  Statistic,
  Badge,
  Space,
  Divider,
  message,
  Alert,
} from "antd";
import {
  SearchOutlined,
  EnvironmentOutlined,
  ShoppingOutlined,
  CarOutlined,
  DeliveredProcedureOutlined,
} from "@ant-design/icons";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { getAllPincodesServiceability } from "../../services/api/pincodeServiceabilityApi";

// Fix for Leaflet marker icons
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

// Custom colored marker icons
const createColoredIcon = (color) => {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      background-color: ${color};
      width: 25px;
      height: 25px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 2px solid white;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [25, 25],
    iconAnchor: [12, 24],
  });
};

const MARKER_COLORS = {
  courier: "#1890ff", // Blue
  ecomm: "#52c41a", // Green
  mcn: "#fa8c16", // Orange
  multiple: "#722ed1", // Purple
};

const { Title, Text } = Typography;
const { Search } = Input;

const PincodeMap = () => {
  const [loading, setLoading] = useState(true);
  const [allData, setAllData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    courier: true,
    ecomm: true,
    mcn: true,
  });
  const [statsInfo, setStatsInfo] = useState({
    total: 0,
    withCoordinates: 0,
    withoutCoordinates: 0,
  });

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Fetch serviceability data from API
        const result = await getAllPincodesServiceability();
        const pincodes = result.data;

        // Filter pincodes with valid lat/lng
        const validPincodes = pincodes.filter(
          (item) =>
            item.latitude !== null &&
            item.longitude !== null &&
            !isNaN(item.latitude) &&
            !isNaN(item.longitude)
        );

        const invalidCount = pincodes.length - validPincodes.length;

        // Transform data
        const transformedData = validPincodes.map((item) => ({
          pincode: item.pincode.toString(),
          courier: item.courier,
          ecomm: item.ecomm,
          mcn: item.mcn,
          lat: parseFloat(item.latitude),
          lng: parseFloat(item.longitude),
        }));

        setAllData(transformedData);
        setStatsInfo({
          total: pincodes.length,
          withCoordinates: validPincodes.length,
          withoutCoordinates: invalidCount,
        });

        message.success(
          `Loaded ${validPincodes.length} pincodes with coordinates from API`
        );

        if (invalidCount > 0) {
          message.warning(
            `${invalidCount} pincodes skipped (missing coordinates)`
          );
        }

        setLoading(false);
      } catch (error) {
        console.error("Error loading data:", error);
        message.error("Failed to load serviceability data");
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filter data based on business line filters and search
  const filteredData = useMemo(() => {
    return allData.filter((item) => {
      // Check if matches any selected business line
      const matchesFilter =
        (filters.courier && item.courier) ||
        (filters.ecomm && item.ecomm) ||
        (filters.mcn && item.mcn);

      // Check if matches search text
      const matchesSearch =
        !searchText || item.pincode.includes(searchText);

      return matchesFilter && matchesSearch;
    });
  }, [allData, filters, searchText]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = allData.length;
    const courier = allData.filter((d) => d.courier).length;
    const ecomm = allData.filter((d) => d.ecomm).length;
    const mcn = allData.filter((d) => d.mcn).length;

    return { total, courier, ecomm, mcn };
  }, [allData]);

  // Determine marker color based on services
  const getMarkerColor = (item) => {
    const services = [item.courier, item.ecomm, item.mcn].filter(Boolean)
      .length;
    if (services > 1) return MARKER_COLORS.multiple;
    if (item.courier) return MARKER_COLORS.courier;
    if (item.ecomm) return MARKER_COLORS.ecomm;
    if (item.mcn) return MARKER_COLORS.mcn;
    return MARKER_COLORS.courier;
  };

  const handleFilterChange = (key) => {
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
          flexDirection: "column",
        }}
      >
        <Spin size="large" tip="Loading pincode data..." />
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <Title level={2}>
        <EnvironmentOutlined /> Pincode Serviceability Dashboard
      </Title>

      {statsInfo.withoutCoordinates > 0 && (
        <Alert
          message="Some Pincodes Missing Coordinates"
          description={`${statsInfo.withoutCoordinates} out of ${statsInfo.total} pincodes don't have latitude/longitude data and are not shown on the map.`}
          type="warning"
          style={{ marginBottom: 20 }}
          closable
        />
      )}

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Serviceable Pincodes"
              value={stats.total}
              prefix={<EnvironmentOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Courier"
              value={stats.courier}
              valueStyle={{ color: MARKER_COLORS.courier }}
              prefix={<CarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="E-commerce"
              value={stats.ecomm}
              valueStyle={{ color: MARKER_COLORS.ecomm }}
              prefix={<ShoppingOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Courier (MCN/3PL)"
              value={stats.mcn}
              valueStyle={{ color: MARKER_COLORS.mcn }}
              prefix={<DeliveredProcedureOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        {/* Filters & Search Panel */}
        <Col xs={24} md={6}>
          <Card title="Filters & Search" bordered={false}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Search
                placeholder="Search pincode"
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                prefix={<SearchOutlined />}
              />

              <Divider style={{ margin: "12px 0" }}>Business Lines</Divider>

              <Checkbox
                checked={filters.courier}
                onChange={() => handleFilterChange("courier")}
              >
                <Badge color={MARKER_COLORS.courier} text="Courier" />
              </Checkbox>

              <Checkbox
                checked={filters.ecomm}
                onChange={() => handleFilterChange("ecomm")}
              >
                <Badge color={MARKER_COLORS.ecomm} text="E-commerce" />
              </Checkbox>

              <Checkbox
                checked={filters.mcn}
                onChange={() => handleFilterChange("mcn")}
              >
                <Badge color={MARKER_COLORS.mcn} text="Courier (MCN/3PL)" />
              </Checkbox>

              <Divider style={{ margin: "12px 0" }}>Legend</Divider>

              <Space direction="vertical" size="small">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <Badge color={MARKER_COLORS.courier} /> Courier only
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <Badge color={MARKER_COLORS.ecomm} /> E-commerce only
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <Badge color={MARKER_COLORS.mcn} /> Courier (MCN/3PL)
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <Badge color={MARKER_COLORS.multiple} /> Multiple services
                </Text>
              </Space>

              <Divider style={{ margin: "12px 0" }}>Results</Divider>

              <Statistic
                title="Showing"
                value={filteredData.length}
                suffix={`/ ${stats.total}`}
                valueStyle={{ fontSize: 18 }}
              />
            </Space>
          </Card>
        </Col>

        {/* Map View */}
        <Col xs={24} md={18}>
          <Card title="Coverage Map" bordered={false} bodyStyle={{ padding: 0 }}>
            <div style={{ height: "700px", width: "100%" }}>
              <MapContainer
                center={[20.5937, 78.9629]}
                zoom={5}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MarkerClusterGroup
                  chunkedLoading
                  maxClusterRadius={20}
                  disableClusteringAtZoom={5}
                  spiderfyOnMaxZoom={true}
                  showCoverageOnHover={false}
                  zoomToBoundsOnClick={true}
                >
                  {filteredData.map((item, idx) => (
                    <Marker
                      key={idx}
                      position={[item.lat, item.lng]}
                      icon={createColoredIcon(getMarkerColor(item))}
                    >
                      <Popup>
                        <div>
                          <strong style={{ fontSize: 16 }}>
                            📍 {item.pincode}
                          </strong>
                          <Divider style={{ margin: "8px 0" }} />
                          <Space direction="vertical" size="small">
                            <Badge
                              status={item.courier ? "success" : "error"}
                              text={`Courier: ${item.courier ? "✓" : "✗"}`}
                            />
                            <Badge
                              status={item.ecomm ? "success" : "error"}
                              text={`E-commerce: ${item.ecomm ? "✓" : "✗"}`}
                            />
                            <Badge
                              status={item.mcn ? "success" : "error"}
                              text={`Courier (MCN/3PL): ${item.mcn ? "✓" : "✗"}`}
                            />
                          </Space>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              </MapContainer>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PincodeMap;
